'use client';

import Link from 'next/link';
import './v4-shell.css';
import { Gamepad2, Send, Mail, ShieldCheck } from 'lucide-react';
import { GlowBackground } from '@/v4/ui';
import { useLang, LEGAL_ENTITY, type Lang } from '@/components/LegalShell';

interface Col { title: string; links: { l: string; h: string }[] }

const COLS: Record<Lang, Col[]> = {
  uk: [
    { title: 'Каталог', links: [{ l: 'Усі ігри', h: '/catalog' }, { l: 'DLC', h: '/catalog?type=dlc' }, { l: 'Знижки', h: '/catalog?sale=1' }] },
    { title: 'Акаунт', links: [{ l: 'Профіль', h: '/account' }, { l: 'Кошик', h: '/cart' }, { l: 'Обране', h: '/wishlist' }] },
    { title: 'Підтримка', links: [{ l: 'FAQ', h: '/faq' }, { l: 'Контакти', h: '/contacts' }, { l: 'Повернення', h: '/refund' }] },
    { title: 'Правова інформація', links: [{ l: 'Публічна оферта', h: '/offer' }, { l: 'Конфіденційність', h: '/privacy' }, { l: 'Угода користувача', h: '/terms' }, { l: 'Про нас', h: '/about' }, { l: 'Відгуки', h: '/reviews' }] },
  ],
  ru: [
    { title: 'Каталог', links: [{ l: 'Все игры', h: '/catalog' }, { l: 'DLC', h: '/catalog?type=dlc' }, { l: 'Скидки', h: '/catalog?sale=1' }] },
    { title: 'Аккаунт', links: [{ l: 'Профиль', h: '/account' }, { l: 'Корзина', h: '/cart' }, { l: 'Избранное', h: '/wishlist' }] },
    { title: 'Поддержка', links: [{ l: 'FAQ', h: '/faq' }, { l: 'Контакты', h: '/contacts' }, { l: 'Возврат', h: '/refund' }] },
    { title: 'Правовая информация', links: [{ l: 'Публичная оферта', h: '/offer' }, { l: 'Конфиденциальность', h: '/privacy' }, { l: 'Пользовательское соглашение', h: '/terms' }, { l: 'О нас', h: '/about' }, { l: 'Отзывы', h: '/reviews' }] },
  ],
  en: [
    { title: 'Catalog', links: [{ l: 'All games', h: '/catalog' }, { l: 'DLC', h: '/catalog?type=dlc' }, { l: 'Deals', h: '/catalog?sale=1' }] },
    { title: 'Account', links: [{ l: 'Profile', h: '/account' }, { l: 'Cart', h: '/cart' }, { l: 'Wishlist', h: '/wishlist' }] },
    { title: 'Support', links: [{ l: 'FAQ', h: '/faq' }, { l: 'Contacts', h: '/contacts' }, { l: 'Refunds', h: '/refund' }] },
    { title: 'Legal', links: [{ l: 'Public Offer', h: '/offer' }, { l: 'Privacy Policy', h: '/privacy' }, { l: 'User Agreement', h: '/terms' }, { l: 'About us', h: '/about' }, { l: 'Reviews', h: '/reviews' }] },
  ],
};

const TAGLINE: Record<Lang, string> = {
  uk: 'Цифрові ігри й товари для Xbox / Microsoft за вигідними цінами.',
  ru: 'Цифровые игры и товары для Xbox / Microsoft по выгодным ценам.',
  en: 'Digital games and goods for Xbox / Microsoft at great prices.',
};
const TG_STORE: Record<Lang, string> = { uk: 'Telegram-магазин', ru: 'Telegram-магазин', en: 'Telegram store' };
const ADMIN: Record<Lang, string> = { uk: 'Адміністрація', ru: 'Администрация', en: 'Administration' };

export function Footer() {
  const lang = useLang();
  const cols = COLS[lang];

  return (
    <footer className="v4f">
      <GlowBackground intensity={0.5} />
      <div className="v4f-inner">
        {/* Contacts strip — one horizontal row */}
        <div className="v4f-contacts">
          <a href="mailto:info@gamessales.vip" className="v4f-contact"><Mail size={15} color="var(--gs-green)" /> info@gamessales.vip</a>
          <a href="https://t.me/games_sales_store" target="_blank" rel="noopener noreferrer" className="v4f-contact"><Send size={15} color="#4aa8e8" /> {TG_STORE[lang]}: @games_sales_store</a>
          <a href="https://t.me/gamessalespro" target="_blank" rel="noopener noreferrer" className="v4f-contact"><ShieldCheck size={15} color="var(--v4-emerald-3)" /> {ADMIN[lang]}: @gamessalespro</a>
        </div>

        {/* Main grid: brand + link columns */}
        <div className="v4f-grid">
          <div className="v4f-brand">
            <Link href="/" className="v4h-logo">
              <div className="v4h-logo-mark">
                <Gamepad2 size={21} color="#fff" />
              </div>
              <span className="v4h-logo-name" style={{ fontSize: 16 }}>Games<em>Sales</em></span>
            </Link>
            <p className="v4f-brand-tag">{TAGLINE[lang]}</p>
          </div>

          <div className="v4f-cols">
            {cols.map(col => (
              <div key={col.title}>
                <div className="v4f-col-title">{col.title}</div>
                {col.links.map(l => (
                  <Link key={l.h + l.l} href={l.h} className="v4f-link">{l.l}</Link>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom — copyright + повні реквізити */}
        <div className="v4f-legal">
          © {new Date().getFullYear()} GamesSales · {LEGAL_ENTITY.name} · ИНН {LEGAL_ENTITY.inn} · ОГРН {LEGAL_ENTITY.ogrn} · {LEGAL_ENTITY.address}
        </div>
      </div>
    </footer>
  );
}
